import { ContentCopy, Download, Print } from '@mui/icons-material'
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material'
import { toCanvas } from 'qrcode'
import { useId, useRef, useState } from 'react'
import { useSnackbar } from 'src/components/snackbar/useSnackbar'

const QR_CANVAS_SIZE = 300
const FONT_SIZE_INITIAL = 18
const FONT_SIZE_MIN = 10
const HORIZONTAL_PADDING = 20
const TEXT_TOP_PADDING = 6
const TEXT_BOTTOM_PADDING = 12
const LINE_SPACING_RATIO = 1.2

type Props = {
  id: string
  name: string
}

const splitTextByWidth = (
  text: string,
  ctx: CanvasRenderingContext2D,
  maxWidth: number,
): string[] => {
  const lines: string[] = []
  let currentLine = ''

  for (const char of Array.from(text)) {
    const candidate = currentLine + char
    if (
      ctx.measureText(candidate).width <= maxWidth ||
      currentLine.length === 0
    ) {
      currentLine = candidate
      continue
    }

    lines.push(currentLine)
    currentLine = char
  }

  if (currentLine) {
    lines.push(currentLine)
  }

  return lines
}

const drawQrWithWrappedText = (
  canvas: HTMLCanvasElement,
  qrCanvas: HTMLCanvasElement,
  text: string,
) => {
  const measureCanvas = document.createElement('canvas')
  const measureCtx = measureCanvas.getContext('2d')
  if (!measureCtx) return

  let fontSize = FONT_SIZE_INITIAL
  const maxWidth = QR_CANVAS_SIZE - HORIZONTAL_PADDING

  const applyFont = (ctx: CanvasRenderingContext2D) => {
    ctx.font = `${fontSize}px Arial`
  }

  applyFont(measureCtx)
  let lines = splitTextByWidth(text, measureCtx, maxWidth)

  while (
    fontSize > FONT_SIZE_MIN &&
    lines.some(line => measureCtx.measureText(line).width > maxWidth)
  ) {
    fontSize -= 1
    applyFont(measureCtx)
    lines = splitTextByWidth(text, measureCtx, maxWidth)
  }

  const lineHeight = fontSize * LINE_SPACING_RATIO
  const textBlockHeight =
    TEXT_TOP_PADDING + lines.length * lineHeight + TEXT_BOTTOM_PADDING

  canvas.width = QR_CANVAS_SIZE
  canvas.height = QR_CANVAS_SIZE + Math.ceil(textBlockHeight)
  const ctx = canvas.getContext('2d')
  if (!ctx) return

  ctx.save()
  ctx.fillStyle = 'white'
  ctx.fillRect(0, 0, canvas.width, canvas.height)

  ctx.drawImage(qrCanvas, 0, 0, QR_CANVAS_SIZE, QR_CANVAS_SIZE)

  applyFont(ctx)
  ctx.textAlign = 'center'
  ctx.textBaseline = 'top'
  ctx.fillStyle = 'black'

  const startY = QR_CANVAS_SIZE + TEXT_TOP_PADDING
  lines.forEach((line, index) => {
    ctx.fillText(line, canvas.width / 2, startY + index * lineHeight)
  })
  ctx.restore()
}

const createMonochromeBitmapBlob = (canvas: HTMLCanvasElement): Blob | null => {
  const ctx = canvas.getContext('2d')
  if (!ctx) return null

  const width = canvas.width
  const height = canvas.height
  const imageData = ctx.getImageData(0, 0, width, height).data
  const WHITE_THRESHOLD = 245
  const bytesPerPixel = 3
  const rowSize = Math.floor((bytesPerPixel * width * 8 + 31) / 32) * 4
  const pixelArraySize = rowSize * height
  const fileSize = 54 + pixelArraySize
  const buffer = new ArrayBuffer(fileSize)
  const view = new DataView(buffer)
  const bytes = new Uint8Array(buffer)

  view.setUint16(0, 0x4d42, true)
  view.setUint32(2, fileSize, true)
  view.setUint32(10, 54, true)
  view.setUint32(14, 40, true)
  view.setInt32(18, width, true)
  view.setInt32(22, -height, true)
  view.setUint16(26, 1, true)
  view.setUint16(28, 24, true)
  view.setUint32(34, pixelArraySize, true)
  view.setInt32(38, 3780, true)
  view.setInt32(42, 3780, true)

  let offset = 54
  const padding = rowSize - width * bytesPerPixel

  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      const index = (y * width + x) * 4
      const r = imageData[index] ?? 0
      const g = imageData[index + 1] ?? 0
      const b = imageData[index + 2] ?? 0
      const a = (imageData[index + 3] ?? 255) / 255

      const blended = (r * 0.299 + g * 0.587 + b * 0.114) * a + 255 * (1 - a)
      const value = blended > WHITE_THRESHOLD ? 255 : 0

      bytes[offset] = value
      bytes[offset + 1] = value
      bytes[offset + 2] = value
      offset += bytesPerPixel
    }
    for (let pad = 0; pad < padding; pad += 1) {
      bytes[offset] = 0
      offset += 1
    }
  }

  return new Blob([buffer], { type: 'image/bmp' })
}

export const QRCodeModal = ({ id, name }: Props) => {
  const [open, setOpen] = useState(false)
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const baseId = useId()
  const { showSuccessSnackbar, showErrorSnackbar } = useSnackbar()

  const handleOpen = () => setOpen(true)
  const handleClose = () => setOpen(false)

  const handlePrint = () => {
    window.print()
  }

  const handleDownloadPng = () => {
    const canvas = canvasRef.current
    if (!canvas) return

    const dataURL = canvas.toDataURL('image/png')
    const link = document.createElement('a')
    link.href = dataURL
    link.download = `${name}_QRコード.png`
    link.click()
  }

  const handleDownloadJpeg = () => {
    const canvas = canvasRef.current
    if (!canvas) return

    const dataURL = canvas.toDataURL('image/jpeg')
    const link = document.createElement('a')
    link.href = dataURL
    link.download = `${name}_QRコード.jpeg`
    link.click()
  }

  const handleDownloadBitmap = () => {
    const canvas = canvasRef.current
    if (!canvas) return

    const blob = createMonochromeBitmapBlob(canvas)
    if (!blob) {
      showErrorSnackbar('BITMAPの生成に失敗しました')
      return
    }

    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `${name}_QRコード.bmp`
    link.click()
    URL.revokeObjectURL(url)
    showSuccessSnackbar('BITMAPのQRコードをダウンロードしました')
  }

  const handleCopy = () => {
    const canvas = document.getElementById(
      `${baseId}-qrcodeCanvas`,
    ) as HTMLCanvasElement | null
    if (!canvas) return
    canvas.toBlob(blob => {
      if (blob) {
        const item = new ClipboardItem({ 'image/png': blob })
        navigator.clipboard.write([item])
        showSuccessSnackbar('QRコードをコピーしました')
      }
    })
  }

  const generateQRCode = () => {
    if (!canvasRef.current) return
    const canvas = canvasRef.current
    const qrCanvas = document.createElement('canvas')
    qrCanvas.width = QR_CANVAS_SIZE
    qrCanvas.height = QR_CANVAS_SIZE

    toCanvas(qrCanvas, id.toString(), { width: QR_CANVAS_SIZE }, error => {
      if (error) return
      drawQrWithWrappedText(canvas, qrCanvas, name)
    })
  }

  return (
    <>
      <style>{`
        @media print {
          body * {
            visibility: hidden;
          }
          [id^="printableQRCode"],
          [id^="printableQRCode"] * {
            visibility: visible;
          }
          [id^="printableQRCode"] {
            position: absolute;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
            width: 100%;
          }
        }
      `}</style>

      <Button variant="text" onClick={handleOpen}>
        表示
      </Button>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        onClick={e => e.stopPropagation()}
        TransitionProps={{
          onEntered: generateQRCode,
        }}
      >
        <DialogTitle>QRコード</DialogTitle>
        <DialogContent>
          <Stack
            id={`${baseId}-printableQRCode`}
            spacing={1}
            sx={{
              alignItems: 'center',
              justifyContent: 'center',
              marginTop: 2,
            }}
          >
            <canvas
              id={`${baseId}-qrcodeCanvas`}
              width={300}
              height={350}
              ref={canvasRef}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ width: '100%', display: 'block' }}>
          <Stack gap={1}>
            <Stack
              direction="row"
              spacing={1}
              width="100%"
              justifyContent="center"
            >
              <Button
                variant="contained"
                onClick={handleDownloadPng}
                startIcon={<Download />}
                size="medium"
              >
                PNG
              </Button>
              <Button
                variant="contained"
                onClick={handleDownloadJpeg}
                startIcon={<Download />}
                size="medium"
              >
                JPEG
              </Button>
              <Button
                variant="contained"
                onClick={handleDownloadBitmap}
                startIcon={<Download />}
                size="medium"
              >
                BITMAP
              </Button>
              <Button
                variant="contained"
                onClick={handleCopy}
                startIcon={<ContentCopy />}
                size="medium"
              >
                コピー
              </Button>
              <Button
                variant="contained"
                onClick={handlePrint}
                startIcon={<Print />}
                size="medium"
              >
                印刷
              </Button>
            </Stack>
            <Button
              variant="text"
              sx={{ marginLeft: 'auto' }}
              onClick={handleClose}
              size="medium"
            >
              <Typography variant="body1" fontWeight="bold" color="primary">
                閉じる
              </Typography>
            </Button>
          </Stack>
        </DialogActions>
      </Dialog>
    </>
  )
}
